import React from 'react';
import { Tooltip } from '@mui/material';

/**
 * Hoists the "can't remove / demote the last super-admin" guardrail copy
 * so the Users page + Roles page can wrap identical tooltips around their
 * respective disabled actions.
 *
 * The backend enforces the guardrail canonically — the client-side count
 * check only exists to disable the button so users aren't confused by a
 * round-trip error. If the two disagree (e.g. another browser demoted the
 * second-to-last super-admin concurrently), the server's 409 wins.
 */
const LastSuperAdminTooltip: React.FC<{
  children: React.ReactElement;
  superAdminCount: number;
  action: 'deactivate' | 'demote' | 'remove';
}> = ({ children, superAdminCount, action }) => {
  const copy = (() => {
    switch (action) {
      case 'deactivate':
        return 'Cannot deactivate the last super-admin. Promote another user first.';
      case 'demote':
        return 'Cannot change the role of the last super-admin. Promote another user first.';
      case 'remove':
        return 'Cannot remove the last super-admin. Promote another user first.';
    }
  })();

  if (superAdminCount > 1) return children;

  return (
    <Tooltip title={copy} placement="top">
      {/* Wrapped span satisfies MUI's disabled-child requirement. */}
      <span style={{ display: 'inline-flex' }}>{children}</span>
    </Tooltip>
  );
};

export default LastSuperAdminTooltip;
